import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MoonStar, Loader2, Play, Pause, Square, Sparkles } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { supabase } from '../../../lib/supabaseClient'
import { useTextToSpeech } from '../../../hooks/useTextToSpeech'
import { cn } from '../../../lib/utils'

// Wind-down audio on /sleep. Generates a soft Egyptian-Arabic bedtime
// piece (sleep-story function), spoken through the existing Azure
// Neural TTS hook (Salma ar-EG + per-session cache + rate control).

const THEMES = ['calm', 'journey', 'reading']

export default function SleepStoryCard() {
  const { t } = useTranslation()
  const tts = useTextToSpeech()
  const [theme, setTheme] = useState('calm')
  const [busy, setBusy] = useState(false)
  const [story, setStory] = useState(null)
  const [failed, setFailed] = useState(false)

  const generate = async () => {
    if (busy) return
    setBusy(true)
    setFailed(false)
    tts.stop()
    const { data, error } = await supabase.functions.invoke('sleep-story', { body: { theme } })
    setBusy(false)
    if (error || !data?.story) { setFailed(true); return }
    setStory(data.story)
  }

  const togglePlay = () => {
    if (!story) return
    if (tts.playing && !tts.paused) tts.pause()
    else if (tts.paused) tts.resume()
    else tts.play(story, 'ar')
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="flex-1 min-w-0 text-sm font-semibold flex items-center gap-1.5 truncate" dir="auto">
            <MoonStar className="w-4 h-4 text-app-primary-500" />
            {t('sleep.story.title')}
          </h2>
          <div className="flex gap-1">
            {THEMES.map((th) => (
              <button
                key={th}
                type="button"
                onClick={() => setTheme(th)}
                className={cn(
                  'px-2 py-1 rounded-md text-[11px] font-semibold transition-colors',
                  theme === th ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground',
                )}
                dir="auto"
              >
                {t(`sleep.story.theme.${th}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={generate}
            disabled={busy}
            className="flex-1 h-10 rounded-md bg-app-primary-500/10 text-app-primary-500 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            dir="auto"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? t('sleep.story.writing') : (story ? t('sleep.story.another') : t('sleep.story.generate'))}
          </button>
          {story && (
            <>
              <button
                type="button"
                onClick={togglePlay}
                aria-label={tts.playing && !tts.paused ? t('sleep.story.pause') : t('sleep.story.play')}
                className="h-10 w-12 rounded-md bg-app-primary-500 text-white inline-flex items-center justify-center"
              >
                {tts.playing && !tts.paused ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 rtl:-scale-x-100" />}
              </button>
              {tts.playing && (
                <button
                  type="button"
                  onClick={() => tts.stop()}
                  aria-label={t('sleep.story.stop')}
                  className="h-10 w-12 rounded-md border border-input text-muted-foreground inline-flex items-center justify-center"
                >
                  <Square className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>

        {failed && <p className="text-xs text-red-500" dir="auto">{t('sleep.story.failed')}</p>}

        {story && (
          <details>
            <summary className="text-[11px] text-muted-foreground cursor-pointer" dir="auto">
              {t('sleep.story.showText')}
            </summary>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed mt-2" dir="auto">{story}</p>
          </details>
        )}
      </CardContent>
    </Card>
  )
}
